import type React from 'react';
import { lazy } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router';

import AppProvider from './providers/app.provider';
import Layout from './providers/layout';
import ProtectedRoute from './providers/protected-route';
import StandalonePageProvider from './providers/standalone-page.provider';
import { ROUTES } from './shared/routes';
import NotFound from './shared/widgets/errors/404';

const Login = lazy(() => import('./pages/Auth/Login/Login'));
const ChangePassword = lazy(
  () => import('./pages/Auth/ChangePassword/ChangePassword')
);
const ResetPassword = lazy(
  () => import('./pages/Auth/ResetPassword/ResetPassword')
);
const Overview = lazy(() => import('./pages/Overview/Overview'));
const Requests = lazy(() => import('./pages/Requests/Requests'));
const NewRequest = lazy(() => import('./pages/NewRequest/NewRequest'));
const Services = lazy(() => import('./pages/Services/Services'));
const ContractOverview = lazy(
  () => import('./pages/ContractOverview/ContractOverview')
);
const RequestDetail = lazy(() => import('./pages/RequestDetail/RequestDetail'));
const Appeals = lazy(() => import('./pages/Appeals/Appeals'));
const AppealDetail = lazy(() => import('./pages/AppealDetail/AppealDetail'));
const VehicleAssessment = lazy(
  () => import('./pages/VehicleAssessment/VehicleAssessment')
);
const NativeMenu = lazy(() => import('./pages/NativeMenu/NativeMenu'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppProvider />,
    children: [
      {
        path: '/:lang',
        children: [
          {
            path: ROUTES.login,
            element: <Login />
          },
          {
            path: ROUTES.changePassword,
            element: <ChangePassword />
          },
          {
            path: ROUTES.resetPassword,
            element: <ResetPassword />
          },
          {
            element: <ProtectedRoute />,
            children: [
              {
                element: <StandalonePageProvider />,
                children: [
                  {
                    path: ROUTES.vehicleAssessment,
                    element: <VehicleAssessment />
                  }
                ]
              },
              {
                element: <Layout />,
                children: [
                  {
                    path: ROUTES.leasing,
                    element: <Overview />
                  },
                  {
                    path: ROUTES.nativeMenu,
                    element: <NativeMenu />
                  },
                  {
                    path: ROUTES.leasingRequests,
                    element: <Requests />
                  },
                  {
                    path: ROUTES.leasingRequestNew,
                    element: <NewRequest />
                  },
                  {
                    path: ROUTES.leasingRequestDetail,
                    element: <RequestDetail />
                  },
                  {
                    path: ROUTES.leasingAppeals,
                    element: <Appeals />
                  },
                  {
                    path: ROUTES.leasingAppealDetail,
                    element: <AppealDetail />
                  },
                  {
                    path: ROUTES.leasingServices,
                    element: <Services />
                  },
                  {
                    path: ROUTES.leasingContract,
                    element: <ContractOverview />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: '*',
        element: (
          <div className="app-router-not-found">
            <NotFound />
          </div>
        )
      }
    ]
  }
]);

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
